import { CloseOutlined } from '@ant-design/icons';
import { $isLinkNode } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isAtNodeEnd } from '@lexical/selection';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  RangeSelection,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import LinkPreview from '../ui/LinkPreview';

const LowPriority = 1;

function getSelectedNode(selection: RangeSelection) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = anchor.getNode();
  const focusNode = focus.getNode();
  if (anchorNode === focusNode) return anchorNode;
  const isBackward = selection.isBackward();
  return isBackward
    ? $isAtNodeEnd(focus)
      ? anchorNode
      : focusNode
    : $isAtNodeEnd(anchor)
    ? focusNode
    : anchorNode;
}

export default function FloatingLinkPreviewPlugin({
  anchorElem,
}: {
  anchorElem: HTMLElement | null;
}) {
  const [editor] = useLexicalComposerContext();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width?: number;
  } | null>(null);
  const [linkUrl, setLinkUrl] = useState<string>('');
  const [hiddenForUrl, setHiddenForUrl] = useState<string | null>(null);

  const update = useCallback(() => {
    const selection = $getSelection();
    const nativeSelection = window.getSelection();
    const root = editor.getRootElement();

    if (
      root &&
      nativeSelection &&
      selection &&
      root.contains(nativeSelection.anchorNode)
    ) {
      if ($isRangeSelection(selection)) {
        const node = getSelectedNode(selection);
        const parent = node.getParent();
        let url = '';
        if ($isLinkNode(parent)) {
          url = parent.getURL();
        } else if ($isLinkNode(node)) {
          url = node.getURL();
        }

        setLinkUrl(url);

        const domRange = nativeSelection.getRangeAt(0);
        const rect = domRange.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        if (rect && rootRect) {
          const top = rect.bottom + window.scrollY + 8;
          const left = rootRect.left + window.scrollX + 8; // align with editor left padding
          const width = Math.max(280, Math.min(520, rootRect.width - 16));
          setPosition({ top, left, width });
        }
      }
    } else {
      setPosition(null);
      setLinkUrl('');
    }
    return true;
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => update());
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          update();
          return false;
        },
        LowPriority
      )
    );
  }, [editor, update]);

  // Reset hide state when url changes
  useEffect(() => {
    if (hiddenForUrl && hiddenForUrl !== linkUrl) {
      setHiddenForUrl(null);
    }
  }, [hiddenForUrl, linkUrl]);

  const card = useMemo(() => {
    if (
      !position ||
      !linkUrl ||
      linkUrl === 'https://' ||
      hiddenForUrl === linkUrl
    )
      return null;

    const style: React.CSSProperties = {
      position: 'absolute',
      top: position.top,
      left: Math.max(8, position.left) + 15,
      zIndex: 1000,
      width: position.width,
    };

    return (
      <div ref={cardRef} style={style}>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setHiddenForUrl(linkUrl)}
            aria-label="Close preview"
            style={{
              position: 'absolute',
              right: 8,
              top: 8,
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 9999,
              border: '1px solid #e5e7eb',
              background: '#ffffff',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
            }}
          >
            <CloseOutlined />
          </button>
          <LinkPreview url={linkUrl} />
        </div>
      </div>
    );
  }, [hiddenForUrl, linkUrl, position]);

  if (!anchorElem || !card) return null;
  return createPortal(card, document.body);
}
